import api from './api';

export interface Student {
  id: number;
  first_name: string;
  last_name: string;
  father_name: string;
  full_name: string;
  phone_number?: string;
  fin_code: string;
  student_status: string;
}

export interface Advertisement {
  id?: number;
  title: string;
  message_type: 'reklam' | 'informativ';
  content: string;
  recipients_data?: Student[];
  recipient_ids: number[];
  company?: number;
  status?: 'hazırlanır' | 'göndərilir' | 'göndərilib' | 'xəta';
  total_recipients?: number;
  successful_sends?: number;
  failed_sends?: number;
  sms_count_per_recipient?: number;
  total_sms_count?: number;
  sms_rules_info?: {
    rules: string[];
    current_sms_count: number;
    character_count: number;
    total_sms_needed: number;
  };
  created_at?: string;
  sent_at?: string;
  created_by?: number;
  created_by_name?: string;
  logs?: AdvertisementLog[];
}

export interface AdvertisementLog {
  id: number;
  recipient: number;
  recipient_name: string;
  phone_number: string;
  status: 'uğurlu' | 'uğursuz' | 'gözləyir';
  error_message?: string;
  sent_at?: string;
  created_at: string;
}

export interface AdvertisementFilters {
  search?: string;
  message_type?: string;
  status?: string;
}

export interface StudentFilters {
  search?: string;
}

export interface SMSCalculation {
  original_content: string;
  normalized_content: string;
  character_count: number;
  sms_count_per_recipient: number;
  recipient_count: number;
  total_sms_needed: number;
  cost_info: string;
}

export interface AdvertisementStatistics {
  total_advertisements: number;
  sent_advertisements: number;
  pending_advertisements: number;
  failed_advertisements: number;
  total_recipients: number;
  total_sms_sent: number;
  success_rate: number;
}

export interface SendResult {
  total: number;
  successful: number;
  failed: number;
  total_sms_sent: number;
}

class AdvertisementAPI {
  // Reklamları əldə et
  async getAdvertisements(filters?: AdvertisementFilters) {
    try {
      const params = new URLSearchParams();
      
      if (filters?.search) params.append('search', filters.search);
      if (filters?.message_type) params.append('message_type', filters.message_type);
      if (filters?.status) params.append('status', filters.status);
      
      const response = await api.get(`/advertisements/?${params.toString()}`);
      
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0,
        message: 'Reklamlar uğurla yükləndi'
      };
    } catch (error: any) {
      console.error('Reklamları yükləmə xətası:', error);
      return {
        success: false,
        data: [],
        count: 0,
        message: error.response?.data?.message || 'Reklamları yükləmə xətası',
        errors: error.response?.data?.errors
      };
    }
  }

  // Tək reklam əldə et
  async getAdvertisement(id: number) {
    try {
      const response = await api.get(`/advertisements/${id}/`);
      
      return {
        success: true,
        data: response.data,
        message: 'Reklam məlumatları yükləndi'
      };
    } catch (error: any) {
      console.error('Reklam yükləmə xətası:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'Reklam yükləmə xətası'
      };
    }
  }

  // Yeni reklam yarat
  async createAdvertisement(advertisementData: Omit<Advertisement, 'id'>) {
    try {
      const response = await api.post('/advertisements/', advertisementData);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Reklam uğurla yaradıldı'
      };
    } catch (error: any) {
      console.error('Reklam yaratma xətası:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'Reklam yaratma xətası',
        errors: error.response?.data?.errors
      };
    }
  }

  // Reklamı yenilə
  async updateAdvertisement(id: number, advertisementData: Partial<Advertisement>) {
    try {
      const response = await api.put(`/advertisements/${id}/`, advertisementData);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Reklam uğurla yeniləndi'
      };
    } catch (error: any) {
      console.error('Reklam yeniləmə xətası:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'Reklam yeniləmə xətası',
        errors: error.response?.data?.errors
      };
    }
  }

  // Reklamı sil
  async deleteAdvertisement(id: number) {
    try {
      await api.delete(`/advertisements/${id}/`);
      
      return {
        success: true,
        message: 'Reklam uğurla silindi'
      };
    } catch (error: any) {
      console.error('Reklam silmə xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Reklam silmə xətası'
      };
    }
  }

  // Tələbələri əldə et (reklam üçün seçim)
  async getStudentsForAdvertisement(filters?: StudentFilters) {
    try {
      const params = new URLSearchParams();
      
      if (filters?.search) params.append('search', filters.search);
      
      const response = await api.get(`/advertisements/students/?${params.toString()}`);
      
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0,
        message: 'Tələbələr uğurla yükləndi'
      };
    } catch (error: any) {
      console.error('Tələbələri yükləmə xətası:', error);
      return {
        success: false,
        data: [],
        count: 0,
        message: error.response?.data?.message || 'Tələbələri yükləmə xətası'
      };
    }
  }

  // Reklamı göndər
  async sendAdvertisement(id: number) {
    try {
      const response = await api.post(`/advertisements/${id}/send/`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Reklam uğurla göndərildi',
        send_result: response.data.send_result
      };
    } catch (error: any) {
      console.error('Reklam göndərmə xətası:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'Reklam göndərmə xətası',
        send_result: error.response?.data?.send_result
      };
    }
  }

  // Reklam loglarını əldə et
  async getAdvertisementLogs(id: number) {
    try {
      const response = await api.get(`/advertisements/${id}/logs/`);
      
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0,
        message: 'Loglar uğurla yükləndi'
      };
    } catch (error: any) {
      console.error('Logları yükləmə xətası:', error);
      return {
        success: false,
        data: [],
        count: 0,
        message: error.response?.data?.message || 'Logları yükləmə xətası'
      };
    }
  }

  // SMS qaydalarını əldə et
  async getSMSRules() {
    try {
      const response = await api.get('/advertisements/sms_rules/');
      
      return {
        success: true,
        data: response.data.data,
        message: 'SMS qaydaları yükləndi'
      };
    } catch (error: any) {
      console.error('SMS qaydalarını yükləmə xətası:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'SMS qaydalarını yükləmə xətası'
      };
    }
  }

  // SMS sayını hesabla
  async calculateSMS(content: string, recipientCount: number = 1): Promise<{
    success: boolean;
    data?: SMSCalculation;
    message: string;
  }> {
    try {
      const response = await api.post('/advertisements/calculate_sms/', {
        content,
        recipient_count: recipientCount
      });
      
      return {
        success: true,
        data: response.data.data,
        message: 'SMS hesablaması tamamlandı'
      };
    } catch (error: any) {
      console.error('SMS hesablama xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'SMS hesablama xətası'
      };
    }
  }

  // Statistikaları əldə et
  async getStatistics() {
    try {
      const response = await api.get('/advertisements/statistics/');
      
      return {
        success: true,
        data: response.data.data,
        message: 'Statistikalar yükləndi'
      };
    } catch (error: any) {
      console.error('Statistikaları yükləmə xətası:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'Statistikaları yükləmə xətası'
      };
    }
  }
}

const advertisementAPI = new AdvertisementAPI();
export default advertisementAPI;